import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { getSharedFoldersByPath, SharedItemResponse } from "../api";
import { toast } from "react-toastify";

export interface FolderNode {
  name: string;
  path: string;
  subfolders: FolderNode[];
  isLoaded?: boolean;
  accessPermission?: boolean;
}

export type BreadcrumbItem = {
  path: string;
  label: string;
};

export interface User {
  type: number;
  login: string;
  fullName: string;
}

interface UserContextProps {
  currentPath: string;
  setCurrentPath: (path: string) => void;
  breadcrumb: BreadcrumbItem[];
  setBreadcrumb: React.Dispatch<React.SetStateAction<BreadcrumbItem[]>>;
  user: User | null;
  setUser: (user: User | null) => void;
  updateSignal: number;
  triggerUpdate: () => void;
  updatePathAndBreadcrumb: (path: string, foldersTree?: FolderNode[]) => void;
  updateUserBreadcrumb: (path: string, foldersTree?: FolderNode[]) => void;
  updateSidebarBreadcrumb: (path: string, foldersTree?: FolderNode[]) => void;
  syncFolder: (
    path: string,
    fetchFolders: (path: string) => Promise<void>
  ) => Promise<void>;
  foldersTree: FolderNode[];
  setFoldersTree: (tree: FolderNode[]) => void;
  userAccessPath: SharedItemResponse[] | null;
  setUserAccessPath: (userPath: SharedItemResponse[] | null) => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentPath, setCurrentPath] = useState<string>(
    sessionStorage.getItem("currentPath") || "Área de Trabalho"
  );

  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { label: "Área de Trabalho", path: "" },
  ]);

  const [user, setUser] = useState<User | null>(
    JSON.parse(sessionStorage.getItem("user") || "null")
  );
  const [updateSignal, setUpdateSignal] = useState<number>(0);
  const [foldersTree, setFoldersTree] = useState<FolderNode[]>([]);

  const [userAccessPath, setUserAccessPath] = useState<
    SharedItemResponse[] | null
  >(null);

  const triggerUpdate = () => setUpdateSignal((prev) => prev + 1);

  const updatePathAndBreadcrumb = useCallback(
    (path: string, tree?: FolderNode[]) => {
      setCurrentPath(path);
  
      const effectiveTree = tree ?? foldersTree;
      const rootFolder = window._env_?.REACT_APP_ROOT_FOLDER || "";
      const breadcrumbPath: BreadcrumbItem[] = [
        { label: "Área de Trabalho", path: "" },
      ];
  
      const index = userAccessPath?.findIndex(item => item.path === path) ?? -1;
  
      const formattedPath = (() => {
        if (index >= 0 && userAccessPath?.[index]) {
          const matchedPath = userAccessPath[index].path;
          return matchedPath.startsWith(rootFolder)
            ? matchedPath.slice(rootFolder.length)
            : matchedPath;
        }
        const parts = path.split("\\");
        return parts.length >= 3 ? `\\\\${parts[2]}` : "\\\\";
      })();
  
      const rootFolderPath = formattedPath.replace(/^\\+/, "\\\\");
      const rootLabel = rootFolderPath.split("\\").pop() || rootFolderPath;
  
      const rootNode = effectiveTree
        .filter(node => path.startsWith(node.path))
        .sort((a, b) => b.path.length - a.path.length)[0];
  
      if (!rootNode) {
        console.warn("⚠️ Caminho não encontrado na árvore. Resetando breadcrumb.");
        setBreadcrumb([
          { label: "Área de Trabalho", path: "" },
          { label: rootLabel, path: rootFolderPath }
        ]);
        return;
      }
  
      breadcrumbPath.push({ label: rootNode.name, path: rootNode.path });
  
      const alreadyHasRoot = breadcrumbPath.some(item => item.label === rootLabel);
      if (!alreadyHasRoot) {
        breadcrumbPath.splice(1, 0, {
          label: rootLabel,
          path: rootFolderPath
        });
      }

      const relativePath = path.replace(rootNode.path, "").replace(/^\\/, "");
      const parts = relativePath ? relativePath.split("\\") : [];
      let accumulatedPath = rootNode.path;
  
      for (const part of parts) {
        accumulatedPath += `\\${part}`;
        breadcrumbPath.push({ label: part, path: accumulatedPath });
      }
  
      setBreadcrumb(breadcrumbPath);
    },
    [foldersTree, setCurrentPath, setBreadcrumb]
  );

  const updateUserBreadcrumb = (path: string, tree?: FolderNode[]) => {
    const effectiveTree = tree ?? foldersTree;
    const breadcrumbPath: BreadcrumbItem[] = [
      { label: "Área de Trabalho", path: "" },
    ];

    const rootNode = effectiveTree
      .filter((node) => path.startsWith(node.path))
      .sort((a, b) => b.path.length - a.path.length)[0];

    if (!rootNode) {
      console.warn(
        "⚠️ Caminho não encontrado na árvore. Resetando breadcrumb."
      );
      setBreadcrumb([{ label: "Área de Trabalho", path: "" }]);
      return;
    }

    const rootPath = rootNode.path;
    const relativePath = path.replace(rootPath, "").replace(/^\\/, "");
    const parts = relativePath ? relativePath.split("\\") : [];

    let accumulatedPath = rootPath;
    breadcrumbPath.push({ label: rootNode.name, path: rootPath });
    for (const part of parts) {
      accumulatedPath += "\\" + part;
      breadcrumbPath.push({ label: part, path: accumulatedPath });
    }

    setBreadcrumb(breadcrumbPath);
  };

  const updateSidebarBreadcrumb = updateUserBreadcrumb;

  const syncFolder = async (
    path: string,
    fetchFolders: (path: string) => Promise<void>
  ) => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      if (!user?.login) {
        toast.error("Usuário não autenticado.");
        return;
      }

      const sharedItems = await getSharedFoldersByPath({
        username: user.login,
        path,
      });

      const fixedItems = sharedItems.map((item) => ({
        ...item,
        path: item.path.replace(/\\\\/g, "\\"),
      }));

      const storedItems: any[] = JSON.parse(
        sessionStorage.getItem("userFolders") || "[]"
      );

      const updatedItems = [
        ...storedItems.filter(
          (item) => !item.path.startsWith(path + "\\") && item.path !== path
        ),
        ...fixedItems,
      ];

      sessionStorage.setItem("userFolders", JSON.stringify(updatedItems));
      await fetchFolders(path);
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      toast.error("Erro ao sincronizar a pasta.");
    }
  };

  useEffect(() => {
    sessionStorage.setItem("currentPath", currentPath);
  }, [currentPath]);

  return (
    <UserContext.Provider
      value={{
        currentPath,
        setCurrentPath,
        breadcrumb,
        setBreadcrumb,
        user,
        setUser,
        updateSignal,
        triggerUpdate,
        updatePathAndBreadcrumb,
        updateUserBreadcrumb,
        updateSidebarBreadcrumb,
        syncFolder,
        foldersTree,
        setFoldersTree,
        userAccessPath,
        setUserAccessPath,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
